import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Resource, Responsible } from "@/shared/types.ts";
import { DirectionEntity } from "@/features/directions/type.ts";
import { CityEntity } from "@/features/city/type.ts";
import ky from "@/shared/api";
import dom from "@/shared/helper/dom.helper.ts";

type CreateRouteRequest = {
  fromCityId: number;
  toCityId: number;
  isActive: boolean;
};

export function useCreateDirection({ onSuccess }: { onSuccess: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<DirectionEntity>, Error, CreateRouteRequest>({
    mutationFn: (data: CreateRouteRequest) =>
      ky.post("/api/v1/directions", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["directions"] });
    },
  });
}

export function useDirections() {
  return useQuery<Resource<DirectionEntity[]>>({
    queryKey: ["directions"],
    queryFn: () => ky<Resource<DirectionEntity[]>>("/api/v1/directions").json(),
    initialData: { data: [] } as { data: DirectionEntity[] },
  });
}

type UpdateRouteRequest = {
  id: number;
  fromCityId: number;
  toCityId: number;
  isActive: boolean;
};

export function useUpdateDirection({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<DirectionEntity>, Error, UpdateRouteRequest>({
    mutationFn: (data: UpdateRouteRequest) =>
      ky.put("/api/v1/directions", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["directions"] });
    },
  });
}

type DeleteDirection = {
  id: number;
};

export function useDeleteDirection({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<DirectionEntity>, Error, DeleteDirection>({
    mutationFn: (data: DeleteDirection) =>
      ky.delete(`/api/v1/directions/${data.id}`, { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["directions"] });
    },
  });
}

export function useDirectionsFrom({ enabled = true }: { enabled?: boolean }) {
  return useQuery<Resource<CityEntity[]>>({
    queryKey: ["directions_from"],
    queryFn: () =>
      ky<Resource<CityEntity[]>>("/api/v1/directions/cities/from").json(),
    initialData: { data: [] } as { data: CityEntity[] },
    enabled,
  });
}

export function useDirectionsTo({
  enabled = true,
  fromCityId,
}: {
  fromCityId?: number;
  enabled?: boolean;
}) {
  return useQuery<Resource<CityEntity[]>>({
    queryKey: [`directions_to_${fromCityId}`],
    queryFn: () =>
      ky<Resource<CityEntity[]>>(
        `/api/v1/directions/cities/to?fromCityId=${fromCityId}`,
      ).json(),
    initialData: { data: [] } as { data: CityEntity[] },
    enabled,
  });
}

type ExportOrderProcessingQrRequest = {
  directionId: number;
};

export function useExportOrderProcessingQr() {
  return useMutation<
    Responsible<string>,
    Error,
    ExportOrderProcessingQrRequest
  >({
    mutationFn: (data: ExportOrderProcessingQrRequest) =>
      ky
        .post(
          "https://gateway.maguya.kz/orders/api/v1/orders/admin/processing/direction/cities",
          {
            json: data,
            timeout: 60 * 1000,
          },
        )
        .json(),
    onSuccess: ({ data }) => {
      dom.openFileFromBase64(data);
    },
  });
}
